import { useCallback } from 'react';
import { DrfData } from '../Pages/AnalisesDrf/types';

interface UsePrintDrfProps {
    data: DrfData | null;
    title: string;
}

interface UsePrintDrfReturn {
    handlePrint: () => void;
    isPrintReady: boolean;
}

export const usePrintDrf = ({ data, title }: UsePrintDrfProps): UsePrintDrfReturn => {
    const isPrintReady = Boolean(data && data.dados_demonstrativo && data.dados_demonstrativo.length > 0);

    const handlePrint = useCallback(() => {
        if (!isPrintReady) {
            alert('Não há dados disponíveis para impressão.');
            return;
        }

        // Cria uma nova janela para impressão
        const printWindow = window.open('', '_blank', 'width=800,height=600');
        
        if (!printWindow) {
            alert('Não foi possível abrir a janela de impressão. Verifique se o bloqueador de pop-ups está desabilitado.');
            return;
        }

        // Gera o HTML para impressão
        const printContent = generatePrintHTML(data!, title);
        
        // Escreve o conteúdo na nova janela
        printWindow.document.write(printContent);
        printWindow.document.close();

        // Aguarda o carregamento e executa a impressão
        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
            
            // Fecha a janela após a impressão (opcional)
            printWindow.onafterprint = () => {
                printWindow.close();
            };
        };
    }, [data, title, isPrintReady]);

    return {
        handlePrint,
        isPrintReady
    };
};

const generatePrintHTML = (data: DrfData, title: string): string => {
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const formatPercentage = (value: number, total: number) => {
        if (total === 0) return '0,00%';
        return ((value / total) * 100).toFixed(2).replace('.', ',') + '%';
    };

    const formatDate = (dateString: string) => {
        if (!dateString || dateString === 'N/A') return 'N/A';
        return dateString;
    };

    // Gera as linhas da tabela
    let tableRows = '';
    

    // Adiciona os dados do demonstrativo
    data.dados_demonstrativo.forEach((grupo) => {
        
        // Tratamento especial para grupos calculados (adoptedSon e childfree)
        if (['adoptedSon', 'childfree'].includes(grupo.grouping_drf_data?.type || '')) {
            const valorTotalGrupoEspecial = grupo.grouping_drf_data?.value || 'R$ 0,00';
            const percentualGrupoEspecial = grupo.grouping_drf_data?.percentual || '0,00%';
            
            // Extrai o valor numérico para verificar se é negativo
            const valorNumerico = parseFloat(valorTotalGrupoEspecial.replace(/[R$\s.]/g, '').replace(',', '.')) || 0;
            const colorStyle = valorNumerico < 0 ? 'color: #d32f2f;' : '';
            
            tableRows += `
                <tr style="background-color: #e3f2fd; border-bottom: 1px solid #ddd;">
                    <td style="font-weight: bold; padding: 6px 8px; font-size: 12px; ${colorStyle}">${grupo.grouping_drf_data.description}</td>
                    <td style="font-weight: bold; padding: 6px 8px; text-align: right; font-size: 12px; ${colorStyle}">${valorTotalGrupoEspecial}</td>
                    <td style="font-weight: bold; padding: 6px 8px; text-align: right; font-size: 12px; ${colorStyle}">${percentualGrupoEspecial}</td>
                </tr>
            `;
            
            // Para grupos 'childfree', não mostra detalhes
            if(grupo.grouping_drf_data?.type === 'childfree') {
                return;
            }
        }

        // Calcula o valor total do grupo
        const valorTotalGrupo = grupo.cfop_de_para.reduce((sum, cfop) => {
            return sum + parseFloat(cfop.valor_total_c170 || '0');
        }, 0);

        // Linha do grupo principal - apenas para grupos normais
        if (!['adoptedSon', 'childfree'].includes(grupo.grouping_drf_data?.type || '')) {
            const colorStyle = valorTotalGrupo < 0 ? 'color: #d32f2f;' : '';
            
            tableRows += `
                <tr style="background-color: #f9f9f9; border-bottom: 1px solid #ddd;">
                    <td style="font-weight: bold; padding: 6px 8px; font-size: 12px; ${colorStyle}">${grupo.grouping_drf_data.description}</td>
                    <td style="font-weight: bold; padding: 6px 8px; text-align: right; font-size: 12px; ${colorStyle}">${formatCurrency(valorTotalGrupo)}</td>
                    <td style="font-weight: bold; padding: 6px 8px; text-align: right; font-size: 12px; ${colorStyle}">${formatPercentage(valorTotalGrupo, data.receita_total)}</td>
                </tr>
            `;
        }

        // Adiciona as operações individuais apenas se houver dados
        if (grupo.cfop_de_para && grupo.cfop_de_para.length > 0) {
            grupo.cfop_de_para.forEach((cfop) => {
                const valorCfop = parseFloat(cfop.valor_total_c170 || '0');
                // Só mostra se o valor não for zero
                if (valorCfop !== 0) {
                    const colorStyle = valorCfop < 0 ? 'color: #d32f2f;' : '';
                    
                    tableRows += `
                        <tr style="border-bottom: 1px solid #eee;">
                            <td style="padding: 4px 8px 4px 24px; font-size: 11px; ${colorStyle}">${cfop.operacao_fechamento_fiscal}</td>
                            <td style="padding: 4px 8px; text-align: right; font-size: 11px; ${colorStyle}">${formatCurrency(valorCfop)}</td>
                            <td style="padding: 4px 8px; text-align: right; font-size: 11px; ${colorStyle}">${formatPercentage(valorCfop, data.receita_total)}</td>
                        </tr>
                    `;
                }
            });
        }
    });

    return `
        <!DOCTYPE html>
        <html lang="pt-BR">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${title} - Impressão</title>
            <style>
                @page {
                    margin: 1cm;
                    size: A4;
                }
                
                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }
                
                body {
                    font-family: Arial, sans-serif;
                    font-size: 12px;
                    line-height: 1.4;
                    color: #000;
                    background: white;
                }
                
                .header {
                    text-align: center;
                    margin-bottom: 20px;
                    border-bottom: 2px solid #333;
                    padding-bottom: 10px;
                }
                
                .header h1 {
                    font-size: 18px;
                    margin-bottom: 5px;
                    color: #333;
                }
                
                .header h2 {
                    font-size: 14px;
                    color: #666;
                    font-weight: normal;
                }
                
                .company-info {
                    margin-bottom: 20px;
                    padding: 10px;
                    background-color: #f9f9f9;
                    border: 1px solid #ddd;
                }
                
                .company-info h3 {
                    font-size: 14px;
                    margin-bottom: 8px;
                    color: #333;
                }
                
                .info-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 8px;
                }
                
                .info-item {
                    font-size: 11px;
                }
                
                .summary {
                    margin-bottom: 20px;
                    text-align: center;
                    padding: 15px;
                    background-color: #e3f2fd;
                    border: 1px solid #1976d2;
                }
                
                .summary h3 {
                    font-size: 14px;
                    margin-bottom: 8px;
                    color: #333;
                }
                
                .summary .value {
                    font-size: 16px;
                    font-weight: bold;
                    color: #1976d2;
                }
                
                .table-container {
                    margin-bottom: 20px;
                }
                
                .table-title {
                    font-size: 14px;
                    font-weight: bold;
                    margin-bottom: 10px;
                    color: #333;
                }
                
                table {
                    width: 100%;
                    border-collapse: collapse;
                    border: 1px solid #ddd;
                }
                
                th {
                    background-color: #f0f0f0;
                    font-weight: bold;
                    padding: 8px;
                    text-align: left;
                    border: 1px solid #ddd;
                    font-size: 12px;
                }
                
                th:nth-child(2),
                th:nth-child(3) {
                    text-align: right;
                }
                
                td {
                    border: 1px solid #eee;
                }
                
                .footer {
                    margin-top: 30px;
                    text-align: center;
                    font-size: 10px;
                    color: #666;
                    border-top: 1px solid #ddd;
                    padding-top: 10px;
                }
                
                @media print {
                    body {
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    
                    .page-break {
                        page-break-before: always;
                    }
                }
            </style>
        </head>
        <body>
            <div class="header">
                <h1>${title}</h1>
            </div>
            
            <div class="company-info">
                <h3>Informações da Empresa</h3>
                <div class="info-grid">
                    <div class="info-item"><strong>Razão Social:</strong> ${data.cabecalho.razao_social}</div>
                    <div class="info-item"><strong>CNPJ:</strong> ${data.cabecalho.cnpj}</div>
                    <div class="info-item"><strong>Período:</strong> ${data.cabecalho.periodo}</div>
                    <div class="info-item"><strong>Data de Geração:</strong> ${formatDate(data.cabecalho.data_geracao)}</div>
                </div>
            </div>
            
            <div class="table-container">
                <div class="table-title">Detalhamento por Operação</div>
                <table>
                    <thead>
                        <tr>
                            <th style="width: 60%;">Descrição</th>
                            <th style="width: 25%; text-align: right;">Valor</th>
                            <th style="width: 15%; text-align: right;">% sobre a receita</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${tableRows}
                    </tbody>
                </table>
            </div>
            
            <div class="footer">
                Relatório gerado em ${new Date().toLocaleString('pt-BR')} | Sistema TRIBUNEX
            </div>
        </body>
        </html>
    `;
};
